import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PaperclipIcon, PaperPlaneRightIcon, XIcon, ChatCircleTextIcon } from "@phosphor-icons/react";
import { fmtTime } from "@/lib/tz";

/**
 * Reusable message inbox — used by Dispatch (admin), DriverPortal, MechanicPortal.
 *
 * Props:
 *  - currentUserId: the caller's user id (used to align bubbles left / right)
 *  - defaultContactId (optional): auto-open a specific thread
 *  - compact (optional): shrink to a smaller card for driver/mechanic views
 */
export default function MessagesPanel({ currentUserId, defaultContactId, compact = false }) {
  const [contacts, setContacts] = useState([]);
  const [roleFilter, setRoleFilter] = useState("all");  // all | driver | mechanic
  const [activeId, setActiveId] = useState(defaultContactId || null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [photo, setPhoto] = useState(null);  // {data, name}
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const bodyRef = useRef(null);

  const loadContacts = async () => {
    try {
      const { data } = await api.get("/messages/contacts");
      setContacts(data || []);
      if (!activeId && data?.length) setActiveId(data[0].id);
    } catch { /* ignore */ }
  };

  const loadThread = async (userId) => {
    if (!userId) return;
    try {
      const { data } = await api.get(`/messages/thread/${userId}`);
      setMessages(data || []);
      // mark thread read
      await api.post(`/messages/thread/${userId}/read`).catch(() => {});
      loadContacts();  // refresh unread counts
      setTimeout(() => bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight }), 60);
    } catch { setMessages([]); }
  };

  useEffect(() => { loadContacts(); }, []);
  useEffect(() => { loadThread(activeId); /* eslint-disable-next-line */ }, [activeId]);

  // Auto-refresh on SSE
  useEffect(() => {
    const h = (e) => {
      if (e?.detail?.entity !== "messages") return;
      loadContacts();
      if (activeId) loadThread(activeId);
    };
    window.addEventListener("rf-data-change", h);
    return () => window.removeEventListener("rf-data-change", h);
    // eslint-disable-next-line
  }, [activeId]);

  const pickPhoto = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Photo too large — 5MB max");
    const reader = new FileReader();
    reader.onload = () => setPhoto({ data: reader.result, name: file.name });
    reader.readAsDataURL(file);
  };

  const send = async (e) => {
    e?.preventDefault();
    if (!activeId || (!text.trim() && !photo)) return;
    setBusy(true);
    try {
      await api.post("/messages", {
        to_user_id: activeId,
        body: text,
        photo: photo?.data || null,
        photo_name: photo?.name || null,
      });
      setText(""); setPhoto(null);
      if (fileRef.current) fileRef.current.value = "";
      loadThread(activeId);
      loadContacts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally {
      setBusy(false);
    }
  };

  const activeContact = contacts.find((c) => c.id === activeId);

  return (
    <div className={`bg-white border border-slate-300 rounded-sm overflow-hidden ${compact ? "" : "shadow-sm"}`} data-testid="messages-panel">
      <div className="border-b border-slate-200 px-4 py-2.5 flex items-center gap-3 flex-wrap">
        <ChatCircleTextIcon size={16} weight="bold" className="text-blue-600" />
        <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-blue-600 font-bold">/// Messages</div>
        {/* Role filter — All / Driver / Mechanic */}
        <div className="ml-auto flex items-center gap-1 text-[10px] font-mono">
          {["all", "driver", "mechanic"].map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRoleFilter(r)}
              data-testid={`msg-filter-${r}`}
              className={`px-2.5 py-1 border rounded-sm uppercase font-bold tracking-wider ${roleFilter === r ? "bg-blue-600 border-blue-600 text-white" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}
            >{r === "all" ? "All" : r === "driver" ? "Drivers" : "Mechanics"}</button>
          ))}
        </div>
      </div>
      <div className={`grid ${compact ? "grid-cols-1 md:grid-cols-[220px_1fr]" : "grid-cols-1 md:grid-cols-[260px_1fr]"} min-h-[420px]`}>
        {/* Contact list */}
        <div className="border-r border-slate-200 max-h-[480px] overflow-y-auto bg-slate-50">
          {(() => {
            const filtered = roleFilter === "all" ? contacts : contacts.filter((c) => (c.role || "").toLowerCase() === roleFilter);
            if (filtered.length === 0) return <div className="p-6 text-center text-xs text-slate-500 font-mono">No {roleFilter === "all" ? "contacts" : roleFilter + "s"}.</div>;
            return filtered.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setActiveId(c.id)}
              className={`w-full text-left px-3 py-2.5 border-b border-slate-200 hover:bg-slate-100 flex items-center gap-2 ${activeId === c.id ? "bg-blue-50" : ""}`}
              data-testid={`msg-contact-${c.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-bold text-slate-800 truncate">{c.name || c.email}</span>
                  {c.unread > 0 && (
                    <span className="bg-red-500 text-white text-[9px] font-mono font-black rounded-full min-w-[16px] h-[16px] inline-flex items-center justify-center px-1" data-testid={`msg-unread-${c.id}`}>{c.unread}</span>
                  )}
                </div>
                <div className="text-[10px] font-mono uppercase text-slate-500">{c.role}</div>
                {c.last_message && (
                  <div className="text-[11px] text-slate-500 truncate mt-0.5">{c.last_message.body || (c.last_message.photo ? "📷 photo" : "")}</div>
                )}
              </div>
            </button>
            ));
          })()}
        </div>

        {/* Thread */}
        <div className="flex flex-col">
          {!activeContact && (
            <div className="flex-1 flex items-center justify-center text-xs text-slate-500 font-mono">Select a contact to start chatting</div>
          )}
          {activeContact && (
            <>
              <div className="px-4 py-2 border-b border-slate-200 flex items-center gap-2">
                <div>
                  <div className="text-sm font-bold text-slate-800">{activeContact.name}</div>
                  <div className="text-[10px] font-mono uppercase text-slate-500">{activeContact.role}</div>
                </div>
              </div>
              <div ref={bodyRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50/40 max-h-[420px]" data-testid="msg-thread-body">
                {messages.length === 0 && <div className="text-xs text-slate-500 font-mono text-center py-6">No messages yet — send the first one.</div>}
                {messages.map((m) => {
                  const mine = m.from_user_id === currentUserId;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-lg px-3 py-2 ${mine ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                        {m.photo && (
                          <img src={m.photo} alt={m.photo_name || ""} className="rounded max-h-56 mb-1.5 cursor-pointer" onClick={() => window.open(m.photo, "_blank")} />
                        )}
                        {m.body && <div className="whitespace-pre-wrap text-sm leading-snug">{m.body}</div>}
                        <div className={`text-[10px] font-mono mt-1 ${mine ? "text-blue-200" : "text-slate-400"}`}>{fmtTime(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <form onSubmit={send} className="border-t border-slate-200 p-3 bg-white">
                {photo && (
                  <div className="mb-2 flex items-center gap-2 bg-slate-100 border border-slate-300 rounded-sm p-1.5">
                    <img src={photo.data} alt="preview" className="h-10 w-10 object-cover rounded-sm" />
                    <div className="text-xs font-mono truncate flex-1">{photo.name}</div>
                    <button type="button" onClick={() => { setPhoto(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-slate-400 hover:text-red-600 p-1">
                      <XIcon size={14} weight="bold" />
                    </button>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => pickPhoto(e.target.files?.[0])} data-testid="msg-photo-input" />
                  <button type="button" onClick={() => fileRef.current?.click()} className="p-2 border border-slate-300 rounded-sm hover:bg-slate-50" title="Attach photo" data-testid="msg-attach-btn">
                    <PaperclipIcon size={14} weight="bold" className="text-slate-600" />
                  </button>
                  <Input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Type a message…"
                    className="flex-1 rounded-sm"
                    data-testid="msg-input"
                  />
                  <Button type="submit" disabled={busy || (!text.trim() && !photo)} className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-bold" data-testid="msg-send-btn">
                    <PaperPlaneRightIcon size={14} weight="bold" />
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
